import { useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  CAMPAIGN_RECIPIENT_PAGE_SIZES,
  type CampaignRecipientListItem,
  type CampaignRecipientPageSize,
} from "@multi-chat/shared";
import {
  TableWorkspace,
  TableWorkspaceFooter,
  TableWorkspaceScroll,
} from "../../components/common/TableWorkspace.js";
import { Button } from "../../components/ui/button/index.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import { cn } from "../../lib/utils.js";
import { CollapsibleChipList } from "../contacts/CollapsibleChipList.js";
import { contactGroupChipClassNameFromKey } from "../contacts/contact-group-color.js";
import {
  contactsTableBodyCellClass,
  contactsTableBodyRowClass,
  contactsTableClass,
  contactsTableHeadRowClass,
  contactsTableHeaderCellClass,
  contactsTableHeaderIconWrapClass,
  contactsTableHeaderLabelClass,
  contactsTableHeaderStickyTopClass,
  contactsTableShellClass,
  contactsValueChipClass,
} from "../contacts/contacts-table-presentation.js";
import { CampaignTablePageSizeSelect } from "./CampaignTablePageSizeSelect.js";
import {
  CAMPAIGN_RECIPIENT_COLUMN_META,
  recipientDisplayName,
  recipientEmail,
  recipientSourceChips,
  type CampaignRecipientColumnId,
  type CampaignSourceChip,
} from "./campaigns-presentation.js";

function sourceChipKey(chip: CampaignSourceChip): string {
  return chip.kind === "GROUP" ? `GROUP-${chip.groupName}` : chip.kind;
}

function RecipientTableHeaderCell({
  columnId,
  label,
}: {
  columnId: CampaignRecipientColumnId;
  label: string;
}): React.JSX.Element {
  const Icon = CAMPAIGN_RECIPIENT_COLUMN_META[columnId].icon;

  return (
    <th className={cn(contactsTableHeaderCellClass, contactsTableHeaderStickyTopClass)}>
      <span className={contactsTableHeaderLabelClass}>
        <span className={contactsTableHeaderIconWrapClass} aria-hidden="true">
          <Icon size={12} />
        </span>
        <span>{label}</span>
      </span>
    </th>
  );
}

const SKELETON_ROW_LIMIT = 10;
const skeletonBarClass =
  "block h-3 animate-pulse rounded bg-surface-muted motion-reduce:animate-none";

function RecipientSkeletonRows({ count }: { count: number }): React.JSX.Element {
  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <tr key={index} className={contactsTableBodyRowClass} aria-hidden="true">
          <td className={contactsTableBodyCellClass}>
            <span className={cn(skeletonBarClass, "w-32 max-w-full")} />
          </td>
          <td className={contactsTableBodyCellClass}>
            <span className={cn(skeletonBarClass, "h-6 w-28 rounded-md")} />
          </td>
          <td className={contactsTableBodyCellClass}>
            <span className={cn(skeletonBarClass, "h-6 w-36 max-w-full rounded-md")} />
          </td>
          <td className={contactsTableBodyCellClass}>
            <span className={cn(skeletonBarClass, "h-6 w-20 rounded-md")} />
          </td>
        </tr>
      ))}
    </>
  );
}

function closestVerticalScroller(node: HTMLElement | null): HTMLElement | null {
  let current = node?.parentElement ?? null;
  while (current) {
    const { overflowY } = window.getComputedStyle(current);
    if (overflowY === "auto" || overflowY === "scroll") return current;
    current = current.parentElement;
  }
  return null;
}

export function CampaignRecipientsTable({
  items,
  loading,
  hasPrevious,
  hasNext,
  pageSize,
  rangeStart,
  rangeEnd,
  total,
  onPageSizeChange,
  onPrevious,
  onNext,
}: {
  items: readonly CampaignRecipientListItem[];
  loading: boolean;
  hasPrevious: boolean;
  hasNext: boolean;
  pageSize: CampaignRecipientPageSize;
  rangeStart: number;
  rangeEnd: number;
  total: number;
  onPageSizeChange: (size: CampaignRecipientPageSize) => void;
  onPrevious: () => void;
  onNext: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);
  const bodyScrollRef = useRef<HTMLDivElement>(null);
  const pendingOuterScrollRef = useRef<{ scroller: HTMLElement; top: number } | null>(null);

  useLayoutEffect(() => {
    const pending = pendingOuterScrollRef.current;
    if (pending && pending.scroller.scrollTop !== pending.top) {
      pending.scroller.scrollTop = pending.top;
    }
    if (loading) return;
    pendingOuterScrollRef.current = null;
    if (bodyScrollRef.current) bodyScrollRef.current.scrollTop = 0;
  }, [items, loading]);

  const paginate = (move: () => void) => {
    if (loading) return;
    const scroller = closestVerticalScroller(rootRef.current);
    pendingOuterScrollRef.current = scroller ? { scroller, top: scroller.scrollTop } : null;
    move();
  };

  const pagerButtonClass =
    "h-8 text-[0.75rem] aria-disabled:cursor-progress aria-disabled:opacity-60";

  return (
    <TableWorkspace
      ref={rootRef}
      className={cn(
        contactsTableShellClass,
        "h-[min(32rem,72vh)] max-h-[min(32rem,72vh)] shrink-0 [overflow-anchor:none]",
      )}
      data-testid="campaign-recipients-table"
    >
      <TableWorkspaceScroll ref={bodyScrollRef} className="[overflow-anchor:none]">
        <table className={cn(contactsTableClass, "min-w-[40rem]")} aria-busy={loading}>
          <thead>
            <tr className={contactsTableHeadRowClass}>
              <RecipientTableHeaderCell columnId="name" label={t("campaigns.recipients.name")} />
              <RecipientTableHeaderCell columnId="phone" label={t("campaigns.recipients.phone")} />
              <RecipientTableHeaderCell columnId="email" label={t("campaigns.recipients.email")} />
              <RecipientTableHeaderCell
                columnId="sources"
                label={t("campaigns.recipients.sources")}
              />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <RecipientSkeletonRows count={Math.min(pageSize, SKELETON_ROW_LIMIT)} />
            ) : items.length === 0 ? (
              <tr>
                <td className={contactsTableBodyCellClass} colSpan={4}>
                  <div className="grid justify-items-start gap-1 py-2" data-testid="campaign-recipients-empty">
                    <p className="m-0 font-medium text-foreground">{t("campaigns.recipients.empty")}</p>
                    <p className="m-0 text-[0.75rem] text-muted">{t("campaigns.recipients.emptyHelp")}</p>
                  </div>
                </td>
              </tr>
            ) : (
              items.map((item) => {
                const chips = recipientSourceChips(item.sourceProvenance);
                const email = recipientEmail(item.personalizationSnapshot);
                return (
                  <tr key={item.id} className={contactsTableBodyRowClass}>
                    <td className={contactsTableBodyCellClass}>
                      <span className="font-medium">{recipientDisplayName(item)}</span>
                    </td>
                    <td className={contactsTableBodyCellClass}>
                      <LtrValue
                        className={cn(contactsValueChipClass, "max-w-full")}
                        title={item.normalizedPhoneE164}
                      >
                        {item.normalizedPhoneE164}
                      </LtrValue>
                    </td>
                    <td className={contactsTableBodyCellClass}>
                      {email ? (
                        <LtrValue className={cn(contactsValueChipClass, "max-w-full")} title={email}>
                          {email}
                        </LtrValue>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className={cn(contactsTableBodyCellClass, "max-w-[18rem]")}>
                      <CollapsibleChipList
                        items={chips}
                        getKey={sourceChipKey}
                        moreLabel={(count) => t("campaigns.recipients.moreSources", { count })}
                        showLessLabel={t("campaigns.recipients.showLess")}
                        testId={`campaign-recipient-sources-${item.id}`}
                        renderChip={(chip) =>
                          chip.kind === "GROUP" ? (
                            <span
                              className={cn(contactGroupChipClassNameFromKey(chip.groupName), "min-w-0")}
                              title={chip.groupName}
                            >
                              <span className="min-w-0 truncate">{chip.groupName}</span>
                            </span>
                          ) : (
                            <span className={contactsValueChipClass}>
                              {t(`campaigns.recipients.source.${chip.kind}`)}
                            </span>
                          )
                        }
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </TableWorkspaceScroll>
      <TableWorkspaceFooter>
        <p
          className="m-0 text-[0.75rem] text-muted tabular-nums"
          data-testid="campaign-recipients-range"
          aria-live="polite"
        >
          {loading ? <span className="sr-only">{t("campaigns.loading")}</span> : null}
          {rangeEnd > 0
            ? t("campaigns.recipients.showing", { start: rangeStart, end: rangeEnd, total })
            : null}
        </p>
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          <CampaignTablePageSizeSelect
            value={pageSize}
            sizes={CAMPAIGN_RECIPIENT_PAGE_SIZES}
            ariaLabel={t("campaigns.recipients.pageSize")}
            testId="campaign-recipient-page-size"
            onChange={(size) => paginate(() => onPageSizeChange(size))}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={pagerButtonClass}
            disabled={!hasPrevious}
            aria-disabled={loading || undefined}
            onClick={() => paginate(onPrevious)}
          >
            {t("campaigns.pagination.previous")}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={pagerButtonClass}
            disabled={!hasNext}
            aria-disabled={loading || undefined}
            onClick={() => paginate(onNext)}
          >
            {t("campaigns.pagination.next")}
          </Button>
        </div>
      </TableWorkspaceFooter>
    </TableWorkspace>
  );
}
